import CanvasElement from 'ui/canvas/CanvasElement';

interface CanvasTableProps {
  maxValue: number;
  targetPowerElectric: number;
  dailyTargetPowerElectric: number[];
  panelArea: { w: number; h: number };
  padding: { left: number; top: number; right: number };
  showTargetPowerElectric: boolean;
  showAverageTargetPowerElectric: boolean;
}

export const CanvasTable = ({
  maxValue,
  targetPowerElectric,
  dailyTargetPowerElectric,
  panelArea,
  padding,
  showTargetPowerElectric,
  showAverageTargetPowerElectric,
}: CanvasTableProps) => {
  const panelPadding = 14;
  const averageTargetPowerElectric =
    (dailyTargetPowerElectric &&
      dailyTargetPowerElectric.reduce((sum, value) => sum + value, 0) / dailyTargetPowerElectric.length) ??
    0;

  const drawCircle = (ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string) => {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  };

  const drawChart = (ctx: CanvasRenderingContext2D) => {
    const chartWidth = ctx.canvas.width - padding.left - padding.right - panelPadding;
    const yAxisLabelPadding = 30;
    const daysWidth = chartWidth / 7;

    // Clear the canvas to start fresh each render
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    //Draw the square
    ctx.fillStyle = '#f5f6f9';
    ctx.strokeStyle = '#E0E0E0';
    ctx.fillRect(padding.left, padding.top, chartWidth, ctx.canvas.height);

    // Draw the grid
    const numberOfYLabels = maxValue / 100;
    for (let i = 0; i <= numberOfYLabels; i++) {
      const yValue = (maxValue / numberOfYLabels) * i; // Calculate the value for the label
      const yPosition = ctx.canvas.height - (yValue / maxValue) * (ctx.canvas.height - padding.top); // Calculate the Y position

      // Draw horizontal grid lines
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
      ctx.moveTo(padding.left, yPosition);
      ctx.lineTo(ctx.canvas.width - padding.right - panelPadding, yPosition);
      ctx.stroke();

      ctx.fillStyle = '#91989d';
      ctx.font = '12px Arial';
      ctx.fillText(
        `${yValue.toFixed(0)} Kw`,
        padding.left - yAxisLabelPadding - ctx.measureText(yValue.toFixed(0)).width,
        yPosition,
      ); // Center text vertically
    }

    // Draw the vertical grid lines
    for (let x = 0; x <= ctx.canvas.width - padding.left; x += daysWidth) {
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
      ctx.moveTo(padding.left + x, ctx.canvas.height);
      ctx.lineTo(padding.left + x, padding.top);
      ctx.stroke();
    }
  };

  const drawTargetPowerElectric = (ctx: CanvasRenderingContext2D) => {
    const chartWidth = ctx.canvas.width - padding.right - panelPadding;
    const yPosition = ctx.canvas.height - (targetPowerElectric / maxValue) * (ctx.canvas.height - padding.top);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    drawCircle(ctx, padding.left, yPosition, 4, '#91989d');

    ctx.beginPath();
    ctx.setLineDash([]);
    ctx.lineWidth = 2;
    ctx.moveTo(padding.left, yPosition);
    ctx.lineTo(chartWidth, yPosition);
    ctx.strokeStyle = '#91989d';
    ctx.stroke();

    drawCircle(ctx, chartWidth, yPosition, 4, '#91989d');
  };

  const drawAverageTargetPowerElectric = (ctx: CanvasRenderingContext2D) => {
    const chartWidth = ctx.canvas.width - padding.right - panelPadding;
    const yPosition = ctx.canvas.height - (averageTargetPowerElectric / maxValue) * (ctx.canvas.height - padding.top);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    drawCircle(ctx, padding.left, yPosition, 4, '#91989d');

    ctx.beginPath();
    ctx.setLineDash([5, 5]);
    ctx.lineWidth = 2;
    ctx.moveTo(padding.left, yPosition);
    ctx.lineTo(chartWidth, yPosition);
    ctx.strokeStyle = '#91989d';
    ctx.stroke();

    drawCircle(ctx, chartWidth, yPosition, 4, '#91989d');
  };

  const drawDailyTargetPowerElectric = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    const chartWidth = ctx.canvas.width - padding.left - padding.right - panelPadding;
    const daysWidth = chartWidth / 7;
    const initialYPosition =
      ctx.canvas.height - (dailyTargetPowerElectric[0] / maxValue) * (ctx.canvas.height - padding.top);
    const finalYPosition =
      ctx.canvas.height - (dailyTargetPowerElectric[6] / maxValue) * (ctx.canvas.height - padding.top);

    ctx.beginPath();
    ctx.setLineDash([]);
    ctx.lineWidth = 2;
    drawCircle(ctx, padding.left, initialYPosition, 3, '#4F9E30');
    for (let i = 0; i <= 7; i++) {
      const startYPosition =
        ctx.canvas.height - (dailyTargetPowerElectric[i] / maxValue) * (ctx.canvas.height - padding.top);
      const endYPosition =
        ctx.canvas.height - (dailyTargetPowerElectric[i + 1] / maxValue) * (ctx.canvas.height - padding.top);
      ctx.moveTo(padding.left + daysWidth * i, startYPosition);
      ctx.lineTo(padding.left + daysWidth * (i + 1), startYPosition);
      ctx.lineTo(padding.left + daysWidth * (i + 1), endYPosition);
      ctx.strokeStyle = '#4F9E30';
      ctx.stroke();
      if (dailyTargetPowerElectric[i - 1] && dailyTargetPowerElectric[i] !== dailyTargetPowerElectric[i - 1]) {
        const yEarlyPosition =
          ctx.canvas.height - (dailyTargetPowerElectric[i - 1] / maxValue) * (ctx.canvas.height - padding.top);
        drawCircle(ctx, padding.left + daysWidth * i, yEarlyPosition, 4, '#4F9E30');
        drawCircle(ctx, padding.left + daysWidth * i, startYPosition, 3, '#4F9E30');
      }
    }

    drawCircle(ctx, padding.left + chartWidth, finalYPosition, 4, '#4F9E30');
  };

  return (
    <>
      <CanvasElement
        draw={drawDailyTargetPowerElectric}
        width={panelArea.w}
        height={(panelArea.h * 3) / 4}
        className="absolute x-0 y-0 z-20"
      />
      {showAverageTargetPowerElectric && (
        <CanvasElement
          draw={drawAverageTargetPowerElectric}
          width={panelArea.w}
          height={(panelArea.h * 3) / 4}
          className="absolute x-0 y-0 z-10"
        />
      )}
      {showTargetPowerElectric && (
        <CanvasElement
          draw={drawTargetPowerElectric}
          width={panelArea.w}
          height={(panelArea.h * 3) / 4}
          className="absolute x-0 y-0 z-10"
        />
      )}
      <CanvasElement draw={drawChart} width={panelArea.w} height={(panelArea.h * 3) / 4} className="relative z-0" />
    </>
  );
};
